import {
  Button,
  EmptyState,
  Pagination,
  ROW_CLS,
  SearchInput,
  Skeleton,
  Surface,
  Table,
  Td,
  Th,
  Tr,
  cn,
} from '@chopchop-se/ui';
import type { SortDirection } from '@warehouse/shared';
import { ChevronDown, ChevronUp, ChevronsUpDown, Filter, Inbox, TriangleAlert } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { errorMessage } from '../lib/notify';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS, nextSort, type ListState } from '../lib/list-query';

/**
 * The list surface EVERY Wave-1 list page uses (ARCHITECTURE §9: "every list:
 * search box + filters + column sort, wired to the shared list-query params").
 *
 * It is deliberately a CONTROLLED component over `ListState` — the same object
 * `listQueryParams()` serializes for the api and `listQueryKey()` turns into a
 * TanStack Query key. So the table, the URL and the request can never disagree
 * about what is being shown; pair it with `useListState()` and the browser's
 * back button works for free.
 *
 * Sorting/filtering/paging are SERVER-side by contract. There is no client-side
 * sort fallback on purpose: a table that silently sorts only the current page
 * is a bug factory on a 40 000-row article register.
 */

export interface DataTableColumn<T> {
  /** Stable id — also the React key for the cell. */
  id: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  /** Server-side sort field name. Present ⇒ the header becomes a sort button. */
  sortField?: string;
  align?: 'left' | 'center' | 'right';
  /** Tailwind width utility, e.g. `w-40`. */
  width?: string;
  className?: string;
  headerClassName?: string;
  /** Hide the column below this breakpoint (tablet-friendly dense tables). */
  hideBelow?: 'sm' | 'md' | 'lg' | 'xl';
}

export interface DataTableProps<T> {
  columns: readonly DataTableColumn<T>[];
  rows: readonly T[];
  rowKey: (row: T) => string;
  state: ListState;
  onStateChange: (next: ListState) => void;

  /** `null` ⇒ keyset mode: the footer offers "show more" instead of pages. */
  total?: number | null;
  nextCursor?: string | null;
  loading?: boolean;
  error?: unknown;

  onRowClick?: (row: T) => void;
  /** Rendered in the toolbar's filter row — selects, date pickers, chips. */
  filters?: ReactNode;
  /** Rendered at the toolbar's right edge — page actions ("New article"). */
  toolbar?: ReactNode;
  searchPlaceholder?: string;
  /** Hide the search box for lists the api does not search. */
  searchable?: boolean;
  emptyTitle?: string;
  emptyDescription?: string;
  emptyAction?: ReactNode;
  pageSizeOptions?: readonly number[];
  searchDebounceMs?: number;
  /** Compact row padding — stocktaking/tablet views. */
  dense?: boolean;
  caption?: string;
}

const ALIGN_CLS: Record<'left' | 'center' | 'right', string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const HIDE_BELOW_CLS: Record<'sm' | 'md' | 'lg' | 'xl', string> = {
  sm: 'hidden sm:table-cell',
  md: 'hidden md:table-cell',
  lg: 'hidden lg:table-cell',
  xl: 'hidden xl:table-cell',
};

export function DataTable<T>({
  columns,
  rows,
  rowKey,
  state,
  onStateChange,
  total = null,
  nextCursor = null,
  loading = false,
  error,
  onRowClick,
  filters,
  toolbar,
  searchPlaceholder,
  searchable = true,
  emptyTitle,
  emptyDescription,
  emptyAction,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  searchDebounceMs = 300,
  dense = false,
  caption,
}: DataTableProps<T>): ReactNode {
  const { t } = useTranslation('shell');
  const captionId = useId();

  const [searchDraft, setSearchDraft] = useState(state.q ?? '');
  const committedQ = useRef(state.q ?? '');

  // Keep the box in sync when the state is changed from outside (URL back
  // button, "clear filters") without stomping on what the user is typing.
  useEffect(() => {
    const incoming = state.q ?? '';
    if (incoming !== committedQ.current) {
      committedQ.current = incoming;
      setSearchDraft(incoming);
    }
  }, [state.q]);

  useEffect(() => {
    if (searchDraft === committedQ.current) return;
    const timer = setTimeout(() => {
      committedQ.current = searchDraft;
      // Any search change resets to page 1 — staying on page 7 of a narrower
      // result set shows an empty table and looks like a broken filter.
      onStateChange({ ...state, q: searchDraft || undefined, page: 1, cursor: undefined });
    }, searchDebounceMs);
    return () => {
      clearTimeout(timer);
    };
    // `state`/`onStateChange` are intentionally NOT dependencies: re-arming the
    // timer on every parent render would mean the debounce never fires. The
    // effect body reads them through the closure of the render that scheduled
    // it, which is the state the user was looking at when they typed.
  }, [searchDraft, searchDebounceMs]);

  const page = state.page ?? 1;
  const pageSize = state.pageSize ?? DEFAULT_PAGE_SIZE;
  const hasFilters = useMemo(
    () => Object.values(state.filters ?? {}).some((v) => v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0)),
    [state.filters],
  );
  const showClear = hasFilters || Boolean(state.q);

  function handleSort(column: DataTableColumn<T>): void {
    if (!column.sortField) return;
    const next = nextSort({ sort: state.sort, dir: state.dir }, column.sortField);
    onStateChange({ ...state, sort: next.sort, dir: next.dir, page: 1, cursor: undefined });
  }

  function handleClear(): void {
    committedQ.current = '';
    setSearchDraft('');
    onStateChange({ ...state, q: undefined, filters: undefined, page: 1, cursor: undefined });
  }

  const colSpan = columns.length;
  const showEmpty = !loading && !error && rows.length === 0;

  return (
    <div className="flex flex-col gap-4">
      {(searchable || filters || toolbar) && (
        <div className="flex flex-wrap items-center gap-3">
          {searchable ? (
            <SearchInput
              variant="outline"
              className="w-full sm:w-72"
              value={searchDraft}
              onChange={setSearchDraft}
              onClear={() => {
                setSearchDraft('');
              }}
              placeholder={searchPlaceholder ?? t('table.searchPlaceholder')}
              aria-label={searchPlaceholder ?? t('table.searchPlaceholder')}
              clearLabel={t('table.clearSearch')}
            />
          ) : null}

          {filters ? (
            <div className="flex flex-wrap items-center gap-2" data-testid="datatable-filters">
              <Filter className="size-4 shrink-0 text-ink-muted" aria-hidden />
              {filters}
            </div>
          ) : null}

          {showClear ? (
            <Button variant="ghost" size="sm" onClick={handleClear}>
              {t('table.clearFilters')}
            </Button>
          ) : null}

          {toolbar ? <div className="ml-auto flex flex-wrap items-center gap-2">{toolbar}</div> : null}
        </div>
      )}

      <Surface className="overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <Table aria-describedby={caption ? captionId : undefined}>
            {caption ? (
              <caption id={captionId} className="sr-only">
                {caption}
              </caption>
            ) : null}
            <thead>
              <tr className="border-b border-surface-border">
                {columns.map((column) => (
                  <Th
                    key={column.id}
                    scope="col"
                    aria-sort={ariaSort(column, state)}
                    className={cn(
                      ALIGN_CLS[column.align ?? 'left'],
                      column.width,
                      column.hideBelow ? HIDE_BELOW_CLS[column.hideBelow] : undefined,
                      column.headerClassName,
                    )}
                  >
                    {column.sortField ? (
                      <button
                        type="button"
                        onClick={() => {
                          handleSort(column);
                        }}
                        title={sortTitle(column, state, (key) => t(key))}
                        className="group inline-flex items-center gap-1.5 uppercase tracking-[0.14em] transition-colors hover:text-ink"
                      >
                        <span>{column.header}</span>
                        <SortGlyph column={column} state={state} />
                      </button>
                    ) : (
                      column.header
                    )}
                  </Th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-border">
              {loading && rows.length === 0
                ? Array.from({ length: 5 }, (_, i) => (
                    <tr key={`skeleton-${i}`}>
                      {columns.map((column) => (
                        <Td key={column.id} className={dense ? 'py-2.5' : undefined}>
                          <Skeleton className="h-3.5 w-full max-w-[12rem] rounded" />
                        </Td>
                      ))}
                    </tr>
                  ))
                : rows.map((row, index) => (
                    <Tr
                      key={rowKey(row)}
                      className={cn(ROW_CLS, onRowClick && 'cursor-pointer')}
                      {...(onRowClick
                        ? {
                            onClick: () => {
                              onRowClick(row);
                            },
                            tabIndex: 0,
                            role: 'button',
                            onKeyDown: (event: KeyboardEvent<HTMLTableRowElement>) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onRowClick(row);
                              }
                            },
                          }
                        : {})}
                    >
                      {columns.map((column) => (
                        <Td
                          key={column.id}
                          className={cn(
                            ALIGN_CLS[column.align ?? 'left'],
                            column.hideBelow ? HIDE_BELOW_CLS[column.hideBelow] : undefined,
                            dense && 'py-2.5',
                            column.className,
                          )}
                        >
                          {column.cell(row, index)}
                        </Td>
                      ))}
                    </Tr>
                  ))}

              {error ? (
                <tr>
                  <td colSpan={colSpan} className="px-5 py-10">
                    <EmptyState
                      bare
                      icon={<TriangleAlert />}
                      title={t('table.loadFailed')}
                      description={errorMessage(error)}
                    />
                  </td>
                </tr>
              ) : null}

              {showEmpty ? (
                <tr>
                  <td colSpan={colSpan} className="px-5 py-10">
                    <EmptyState
                      bare
                      icon={<Inbox />}
                      title={emptyTitle ?? t('table.empty')}
                      description={emptyDescription ?? (showClear ? t('table.emptyDescription') : undefined)}
                      action={emptyAction}
                    />
                  </td>
                </tr>
              ) : null}
            </tbody>
          </Table>
        </div>

        {total !== null && total > 0 ? (
          <div className="border-t border-surface-border px-2 py-1">
            <Pagination
              page={page}
              pageSize={pageSize}
              total={total}
              ofLabel={t('table.of')}
              pageSizeOptions={[...pageSizeOptions]}
              onPageChange={(next) => {
                onStateChange({ ...state, page: next, cursor: undefined });
              }}
              onPageSizeChange={(size) => {
                onStateChange({ ...state, pageSize: size, page: 1, cursor: undefined });
              }}
            />
          </div>
        ) : null}

        {total === null && nextCursor ? (
          <div className="flex justify-center border-t border-surface-border p-3">
            <Button
              variant="secondary"
              size="sm"
              loading={loading}
              onClick={() => {
                onStateChange({ ...state, cursor: nextCursor });
              }}
            >
              {t('table.loadMore')}
            </Button>
          </div>
        ) : null}
      </Surface>
    </div>
  );
}

function ariaSort<T>(column: DataTableColumn<T>, state: ListState): 'ascending' | 'descending' | 'none' | undefined {
  if (!column.sortField) return undefined;
  if (state.sort !== column.sortField) return 'none';
  return state.dir === 'desc' ? 'descending' : 'ascending';
}

function sortTitle<T>(column: DataTableColumn<T>, state: ListState, t: (key: string) => string): string {
  const active = state.sort === column.sortField;
  const dir: SortDirection | undefined = active ? (state.dir ?? 'asc') : undefined;
  if (!active) return t('table.sortAscending');
  return dir === 'asc' ? t('table.sortDescending') : t('table.sortNone');
}

function SortGlyph<T>({ column, state }: { column: DataTableColumn<T>; state: ListState }): ReactNode {
  const active = state.sort === column.sortField;
  if (!active) {
    return <ChevronsUpDown className="size-3.5 opacity-0 transition-opacity group-hover:opacity-60" aria-hidden />;
  }
  return state.dir === 'desc' ? (
    <ChevronDown className="size-3.5 text-brand-600" aria-hidden />
  ) : (
    <ChevronUp className="size-3.5 text-brand-600" aria-hidden />
  );
}
